import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { BeeldResultaat, BibliotheekBeeld } from '../../../api/_lib/beeldbibliotheek-zoeken';
import { marketingFetch } from '../../stores/marketing-store';
import { MarketingNav, MarketingPageHeader } from './shared';
import { FotostudioDialog } from '../../components/marketing/FotostudioDialog';
import { fotostudio, type StudioCapabilities } from '../../lib/fotostudio-api';

const BUTTON = 'rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40';
const INPUT = 'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900';
const MERKEN: Record<string, string> = {
  kleda: 'Kleda', cinco: 'Cinco', sofasaver: 'SofaSaver', schoonhuisshop: 'Schoonhuisshop',
  vloerkleedreinigen: 'Vloerkleedreinigen.com', bankreinigen: 'Bankreinigen.nl',
};

function BeeldFoto({ beeld, groot = false }: { beeld: BibliotheekBeeld; groot?: boolean }) {
  const [fout, setFout] = useState(false);
  return fout ? (
    <div className="flex h-full min-h-40 items-center justify-center bg-gray-100 p-6 text-sm text-gray-500">
      Preview niet beschikbaar voor afbeelding {beeld.nummer}
    </div>
  ) : (
    <img src={beeld.maten[groot ? 'werkbestand' : 'mail'].url} alt={beeld.alt_tekst}
      loading={groot ? 'eager' : 'lazy'} decoding="async" onError={() => setFout(true)}
      className={groot ? 'max-h-[65vh] w-full object-contain' : 'h-full w-full object-contain transition-transform group-hover:scale-[1.03]'} />
  );
}

function BeeldPreview({ beeld, onClose }: { beeld: BibliotheekBeeld; onClose: () => void }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const [melding, setMelding] = useState('');
  const [kopieTekst, setKopieTekst] = useState('');
  const maat = beeld.maten.werkbestand;
  const verwijzing = `Afbeelding ${beeld.nummer}`;

  useEffect(() => {
    const el = dialog.current;
    el?.showModal();
    return () => el?.close();
  }, []);

  async function kopieer(tekst: string, label: string) {
    try {
      await navigator.clipboard.writeText(tekst);
      setKopieTekst('');
      setMelding(`${label} gekopieerd`);
    } catch {
      setKopieTekst(tekst);
      setMelding('Automatisch kopiëren lukt niet. Selecteer en kopieer de tekst hieronder.');
    }
  }

  return (
    <dialog ref={dialog} onCancel={onClose} onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}
      aria-labelledby="beeld-titel" className="m-auto max-h-[92vh] w-[min(1100px,94vw)] overflow-auto rounded-2xl bg-white p-0 shadow-xl backdrop:bg-black/60">
      <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-6 py-4">
        <div><p className="text-sm font-semibold text-indigo-600">{verwijzing}</p>
          <h2 id="beeld-titel" className="mt-1 text-lg font-semibold text-gray-900">{beeld.hoofdonderwerp}</h2></div>
        <button autoFocus onClick={onClose} className={BUTTON}>Sluiten</button>
      </div>
      <div className="grid gap-6 p-6 lg:grid-cols-[minmax(0,2fr)_minmax(240px,1fr)]">
        <div className="flex items-center rounded-xl bg-gray-100"><BeeldFoto beeld={beeld} groot /></div>
        <div className="space-y-5 text-sm">
          <div className="rounded-xl bg-indigo-50 p-4">
            <p className="font-medium text-indigo-950">Gebruik dit nummer in je opdracht</p>
            <p className="mt-2 text-indigo-900">Vervang de afbeelding op [pagina-URL] met afbeelding {beeld.nummer}.</p>
            <button className={`${BUTTON} mt-3`} onClick={() => kopieer(verwijzing, 'Afbeeldingsnummer')}>Kopieer afbeeldingsnummer</button>
          </div>
          <dl className="space-y-3">
            <div><dt className="text-gray-500">Afmetingen</dt><dd>{maat.breedte} × {maat.hoogte} px</dd></div>
            <div><dt className="text-gray-500">Beeldkwaliteit</dt><dd>{beeld.web_score}/5{beeld.web_score === 5 ? ' · geschikt als hoofdbeeld' : ''}</dd></div>
            <div><dt className="text-gray-500">Past bij</dt><dd>{beeld.merk_fit.map((m) => MERKEN[m] ?? m).join(', ') || 'Alle merken'}</dd></div>
            <div><dt className="text-gray-500">Gebruik</dt><dd>{beeld.pagina_types.join(', ') || 'Algemeen'}</dd></div>
            <div><dt className="text-gray-500">Alt-tekst</dt><dd>{beeld.alt_tekst}</dd></div>
          </dl>
          <div className="flex flex-wrap gap-2">
            <button className={BUTTON} onClick={() => kopieer(maat.url, 'Afbeeldings-URL')}>Kopieer afbeeldings-URL</button>
            <button className={BUTTON} onClick={() => kopieer(`${window.location.origin}/marketing/beeldbibliotheek?zoek=${beeld.nummer}`, 'Bibliotheeklink')}>Kopieer bibliotheeklink</button>
            <a className={BUTTON} href={maat.url} target="_blank" rel="noreferrer">Open volledig beeld</a>
          </div>
          <p role="status" className="text-gray-600">{melding}</p>
          {kopieTekst && <textarea aria-label="Tekst om te kopiëren" readOnly value={kopieTekst} onFocus={(e) => e.target.select()} className={INPUT} rows={3} />}
        </div>
      </div>
    </dialog>
  );
}

export function BeeldbibliotheekPage() {
  const [studioOpen, setStudioOpen] = useState(false);
  const [studioCapabilities, setStudioCapabilities] = useState<StudioCapabilities | null>(null);
  useEffect(() => { fotostudio.capabilities().then(setStudioCapabilities).catch(() => {}); }, []);
  const [params, setParams] = useSearchParams();
  const query = params.toString();
  const [zoek, setZoek] = useState(params.get('zoek') ?? params.get('nummer') ?? '');
  const [resultaat, setResultaat] = useState<BeeldResultaat | null>(null);
  const [laden, setLaden] = useState(true);
  const [fout, setFout] = useState<string | null>(null);
  const [herladen, setHerladen] = useState(0);
  const [preview, setPreview] = useState<BibliotheekBeeld | null>(null);

  useEffect(() => { setZoek(params.get('zoek') ?? params.get('nummer') ?? ''); }, [params]);
  useEffect(() => {
    let actief = true;
    setLaden(true);
    setFout(null);
    marketingFetch<BeeldResultaat>(`/api/marketing/beeldbibliotheek?${query}`)
      .then((data) => { if (actief) setResultaat(data); })
      .catch((err: unknown) => { if (actief) setFout(err instanceof Error ? err.message : 'Beelden laden lukt niet'); })
      .finally(() => { if (actief) setLaden(false); });
    return () => { actief = false; };
  }, [query, herladen]);

  function filter(naam: string, waarde: string) {
    const volgende = new URLSearchParams(params);
    volgende.delete('offset');
    volgende.delete('nummer');
    if (waarde) volgende.set(naam, waarde); else volgende.delete(naam);
    setParams(volgende);
  }

  function blader(offset: number) {
    const volgende = new URLSearchParams(params);
    volgende.set('offset', String(offset));
    setParams(volgende);
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-gray-50">
      <MarketingNav />
      <MarketingPageHeader title="Beeldbibliotheek" subtitle="Kies een beeld en gebruik het vaste afbeeldingsnummer in je opdracht." />
      {studioCapabilities?.enabled && <button className={BUTTON} onClick={() => setStudioOpen(true)}>Fotostudio</button>}
      {studioOpen && studioCapabilities && <FotostudioDialog capabilities={studioCapabilities} onClose={() => setStudioOpen(false)} onReady={() => setHerladen(n => n + 1)} />}
      <div className="space-y-5 px-6 pb-8">
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <form onSubmit={(event) => { event.preventDefault(); filter('zoek', zoek.trim()); }} className="flex gap-2">
            <label className="flex-1"><span className="sr-only">Zoek op onderwerp of afbeeldingsnummer</span>
              <input type="search" value={zoek} onChange={(event) => setZoek(event.target.value)} placeholder="Zoek op onderwerp of nummer, bijvoorbeeld wasserij of 127" className={INPUT} />
            </label>
            <button type="submit" className="rounded-lg bg-indigo-600 px-5 py-2 text-sm font-medium text-white hover:bg-indigo-700">Zoeken</button>
          </form>
          <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <label className="space-y-1 text-xs text-gray-500">Merk
              <select aria-label="Merk" value={params.get('merk') ?? ''} onChange={(e) => filter('merk', e.target.value)} className={INPUT}>
                <option value="">Alle merken</option>{resultaat?.merken.map((m) => <option key={m} value={m}>{MERKEN[m] ?? m}</option>)}
              </select></label>
            <label className="space-y-1 text-xs text-gray-500">Gebruik
              <select aria-label="Gebruik" value={params.get('pagina') ?? ''} onChange={(e) => filter('pagina', e.target.value)} className={INPUT}>
                <option value="">Alle toepassingen</option>{resultaat?.paginatypes.map((p) => <option key={p} value={p}>{p}</option>)}
              </select></label>
            <label className="space-y-1 text-xs text-gray-500">Vorm
              <select aria-label="Vorm" value={params.get('vorm') ?? ''} onChange={(e) => filter('vorm', e.target.value)} className={INPUT}>
                <option value="">Alle vormen</option><option value="liggend">Liggend</option><option value="staand">Staand</option><option value="vierkant">Vierkant</option>
              </select></label>
            <label className="space-y-1 text-xs text-gray-500">Beeldkwaliteit
              <select aria-label="Beeldkwaliteit" value={params.get('minScore') ?? ''} onChange={(e) => filter('minScore', e.target.value)} className={INPUT}>
                <option value="">Alle kwaliteiten</option><option value="4">Minimaal 4/5</option><option value="5">5/5 · hoofdbeeld</option>
              </select></label>
          </div>
        </div>
        <div className="flex items-center justify-between gap-3 text-sm text-gray-500">
          <p aria-live="polite">{laden ? 'Beelden laden…' : fout ? 'Laden mislukt' : `${resultaat?.totaal ?? 0} van ${resultaat?.bibliotheekTotaal ?? 0} beelden`}</p>
          {query && <button onClick={() => setParams({})} className="font-medium text-indigo-600 hover:underline">Toon alle beelden</button>}
        </div>
        {fout && <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-5 text-sm text-red-800">
          <p>{fout}</p><button className={`${BUTTON} mt-3`} onClick={() => setHerladen((n) => n + 1)}>Opnieuw proberen</button>
        </div>}
        {laden && <div className="grid grid-cols-2 gap-4 lg:grid-cols-3 2xl:grid-cols-4" aria-hidden="true">
          {Array.from({ length: 8 }, (_, i) => <div key={i} className="aspect-[4/3] animate-pulse rounded-xl bg-gray-200" />)}
        </div>}
        {!laden && !fout && resultaat && <>
          {resultaat.beelden.length === 0 && <div className="rounded-xl border border-gray-200 bg-white p-10 text-center text-gray-600">
            <p>Geen beelden gevonden. Probeer een ander onderwerp of wis de filters.</p>
            <button className={`${BUTTON} mt-4`} onClick={() => setParams({})}>Toon alle beelden</button>
          </div>}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
            {resultaat.beelden.map((beeld) => <button key={beeld.nummer} onClick={() => setPreview(beeld)}
              aria-label={`Bekijk afbeelding ${beeld.nummer}: ${beeld.hoofdonderwerp}`}
              className="group overflow-hidden rounded-xl border border-gray-200 bg-white text-left shadow-sm transition hover:border-indigo-300 hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-600">
              <div className="relative aspect-[4/3] overflow-hidden bg-gray-100 p-2">
                <BeeldFoto beeld={beeld} />
                <span className="absolute left-3 top-3 rounded-md bg-white/95 px-2.5 py-1 text-sm font-semibold tabular-nums text-indigo-700 shadow-sm">#{beeld.nummer}</span>
              </div>
              <div className="p-4"><p className="line-clamp-2 min-h-10 text-sm font-medium text-gray-900">{beeld.hoofdonderwerp}</p>
                <p className="mt-2 text-xs text-gray-500">{beeld.maten.werkbestand.breedte} × {beeld.maten.werkbestand.hoogte} px · kwaliteit {beeld.web_score}/5</p>
              </div>
            </button>)}
          </div>
          {resultaat.totaal > resultaat.limit && <div className="flex items-center justify-between gap-3 border-t border-gray-200 pt-5">
            <button className={BUTTON} disabled={resultaat.offset === 0} onClick={() => blader(Math.max(0, resultaat.offset - resultaat.limit))}>Vorige</button>
            <p className="text-sm text-gray-500">Pagina {Math.floor(resultaat.offset / resultaat.limit) + 1} van {Math.ceil(resultaat.totaal / resultaat.limit)}</p>
            <button className={BUTTON} disabled={resultaat.offset + resultaat.limit >= resultaat.totaal} onClick={() => blader(resultaat.offset + resultaat.limit)}>Volgende</button>
          </div>}
        </>}
      </div>
      {preview && <BeeldPreview key={preview.nummer} beeld={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}
